export default class AuthJwtServices {
  axiosIns = null

  jwtConfig = null

  constructor(axiosIns, jwtConfig) {
    this.axiosIns = axiosIns;
    this.jwtConfig = jwtConfig;
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setMfaToken(value) {
    localStorage.setItem(this.jwtConfig.storageMFATokenKeyName, value);
  }

  setPasswordResetToken(token) {
    localStorage.setItem(this.jwtConfig.storagePasswordResetTokenKeyName, token)
  }

  setAccountConfirmationToken(token) {
    localStorage.setItem(this.jwtConfig.storageConfirmAccountTokenKeyName, token);
  }

  setPhoneVerificationToken(token) {
    localStorage.setItem(this.jwtConfig.storageConfirmPhoneToken, token);
  }

  setPhoneVerificationTimestamp(unix_timestamp) {
    localStorage.setItem(this.jwtConfig.storageConfirmPhoneTimestamp, unix_timestamp);
  }

  getPhoneVerificationToken(token) {
    return localStorage.getItem(this.jwtConfig.storageConfirmPhoneToken, token);
  }

  getPhoneVerificationTimestamp() {
    return localStorage.getItem(this.jwtConfig.storageConfirmPhoneTimestamp);
  }

  getAccessToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getMfaAccessToken() {
    return localStorage.getItem(this.jwtConfig.storageMFATokenKeyName);
  }

  getAccountConfirmationToken() {
    return localStorage.getItem(this.jwtConfig.storageConfirmAccountTokenKeyName)
  }

  getPasswordResetToken() {
    return localStorage.getItem(this.jwtConfig.storagePasswordResetTokenKeyName)
  }

  clearAccessToken() {
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
  }

  clearAccountConfirmationToken() {
    localStorage.removeItem(this.jwtConfig.storageConfirmAccountTokenKeyName);
  }

  clearPasswordResetToken() {
    localStorage.removeItem(this.jwtConfig.storagePasswordResetTokenKeyName);
  }

  clearPhoneVerificationTimestamp() {
    localStorage.removeItem(this.jwtConfig.storageConfirmPhoneTimestamp);
  }

  login(...args) {
    const tokenNames = [
      this.jwtConfig.storageConfirmAccountTokenKeyName,
      this.jwtConfig.storagePasswordResetTokenKeyName,
      this.jwtConfig.storageMFATokenKeyName,
      this.jwtConfig.storageTokenKeyName,
      this.jwtConfig.storageMFADeactivationToken,
      this.jwtConfig.storageMFAPasswordResetToken,
      this.jwtConfig.storageRefreshTokenKeyName
    ];
    setTimeout(200, () => {
      tokenNames.forEach(tokenName => {
        localStorage.removeItem(tokenName);
      })
    })

    return this.axiosIns.post(this.jwtConfig.auth.loginEndpoint, ...args)
  }

  logout(...args) {
    return this.axiosIns.post(this.jwtConfig.auth.logoutEndpoint, ...args)
  }

  logPageAccess(payload = {}) {
    return this.axiosIns.post(this.jwtConfig.auth.logsEndpoint, payload)
  }

  getRegistrationSettings() {
    return this.axiosIns.get(`${this.jwtConfig.auth.registrationSettingsEndpoint}`)
  }

  confirmTokenValidity(tokenType) {
    let token = null;
    if (tokenType === "web_token") {
      this.clearPasswordResetToken();
      this.clearAccountConfirmationToken();
      token = this.getAccessToken()
    } else if (tokenType === "password_reset_token") {
      this.clearAccessToken();
      this.clearAccountConfirmationToken();
      token = this.getPasswordResetToken()
    } else if (tokenType === "confirm_account_token") {
      this.clearAccessToken();
      this.clearPasswordResetToken();
      token = this.getAccountConfirmationToken()
    }
    return this.axiosIns.post(this.jwtConfig.auth.confirmTokenValidityEndpoint, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  confirmAccount() {
    this.clearAccessToken();
    this.clearPasswordResetToken();
    return this.axiosIns.post(this.jwtConfig.auth.confirmEmailEndpoint, {}, {
      headers: {
        Authorization: `Bearer ${this.getAccountConfirmationToken()}`
      }
    });
  }

  verifyEmail(token) {
    return this.axiosIns.post(this.jwtConfig.auth.confirmEmailEndpoint, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  register(formData) {
    return this.axiosIns.post(this.jwtConfig.auth.registerEndpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  }

  resendAccountConfirmationEmail(payload) {
    return this.axiosIns.post(this.jwtConfig.auth.resendConfirmEmailEndpoint, payload);
  }

  requestPasswordReset(...args) {
    return this.axiosIns.post(this.jwtConfig.auth.requestPasswordResetEndpoint, ...args);
  }

  resetPassword(...args) {
    this.clearAccessToken();
    this.clearAccountConfirmationToken();
    return this.axiosIns.post(this.jwtConfig.auth.resetPasswordEndpoint, ...args, {
      headers: {
        Authorization: `Bearer ${this.getPasswordResetToken()}`
      }
    });
  }

  requestPhoneVerification(...args) {
    this.clearPasswordResetToken();
    this.clearAccountConfirmationToken();
    return this.axiosIns.post(this.jwtConfig.auth.requestPhoneVerificationEndpoint, ...args);
  }

  confirmPhoneVerification(...args) {
    this.clearPasswordResetToken();
    this.clearAccountConfirmationToken();
    return this.axiosIns.post(this.jwtConfig.auth.confirmPhoneEndpoint, ...args, {
      headers: {
        Authorization: `Bearer ${this.getPhoneVerificationToken()}`
      }
    });
  }

  saveOnboardingData(formData) {
    return this.axiosIns.post(`${this.jwtConfig.auth.onboardingEndpoint}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  }

  updateProfile(payload = {}) {
    return this.axiosIns.put(this.jwtConfig.auth.updateUserProfileEndpoint, payload, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
  }

  getProfile() {
    return this.axiosIns.get(this.jwtConfig.auth.updateUserProfileEndpoint, {})
  }

  updateProfileAvatar(payload = {}) {
    return this.axiosIns.post(this.jwtConfig.auth.updateUserProfileEndpoint + '/avatar', payload, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
  }

  fetchUserNotifications(params) {
    return this.axiosIns.get(`${this.jwtConfig.auth.userNotificationsEndpoint}`, {
      params
    })
  }

  changePassword(payload) {
    return this.axiosIns.post(this.jwtConfig.auth.changePasswordEndpoint, payload);
  }

  fetchMinistersWife() {
    return this.axiosIns.get(`${this.jwtConfig.auth.ministersWifeEndpoint}`);
  }

  saveMinistersWife(payload) {
    return this.axiosIns.post(`${this.jwtConfig.auth.ministersWifeEndpoint}`, payload);
  }
}
